(() => {
  'use strict';

  if (window.JblReviewMedia?.create) return;
  const instances = new WeakMap();

  function create(page) {
    if (!page?.querySelector) return null;
    if (instances.has(page)) return instances.get(page);

    const dialog = page.querySelector('[data-review-photo-dialog]');
    const dataElement = page.querySelector('[data-review-data]');
    if (!dialog || !dataElement) return null;
    const t = window.JblText.create(page);

    let reviews;
    try {
      const parsed = JSON.parse(dataElement.textContent);
      reviews = Array.isArray(parsed) ? parsed : parsed.reviews;
      if (!Array.isArray(reviews)) return null;
    } catch {
      return null;
    }

    const image = dialog.querySelector('[data-review-photo-image]');
    const author = dialog.querySelector('[data-review-photo-author]');
    const counter = dialog.querySelector('[data-review-photo-counter]');
    const previous = dialog.querySelector('[data-review-photo-previous]');
    const next = dialog.querySelector('[data-review-photo-next]');
    const closeButton = dialog.querySelector('[data-review-photo-close]');
    const viewport = dialog.querySelector('[data-review-photo-viewport]');
    const status = dialog.querySelector('[data-review-photo-status]');
    if (!image || !author || !counter || !previous || !next || !closeButton || !viewport) return null;

    let photos = [];
    let photoIndex = 0;
    let reviewAuthor = '';
    let previouslyFocused = null;
    let previousOverflow = '';
    let scrollLocked = false;
    let touchStart = null;

    const isOpen = () => dialog.hasAttribute('open');
    const setStatus = (text) => { if (status) status.textContent = text; };
    const restoreFocusAndScroll = () => {
      if (scrollLocked) {
        document.documentElement.style.overflow = previousOverflow;
        scrollLocked = false;
      }
      touchStart = null;
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
      previouslyFocused = null;
    };
    const close = () => {
      if (!isOpen()) return;
      if (typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        restoreFocusAndScroll();
      }
    };

    function normalizePhoto(photo) {
      if (!photo || typeof photo.src !== 'string' || !photo.src.trim()) return null;
      try {
        const url = new URL(photo.src, document.baseURI);
        if (!['https:', 'http:'].includes(url.protocol)) return null;
        return { src: url.href, alt: typeof photo.alt === 'string' ? photo.alt : '', width: Number(photo.width), height: Number(photo.height) };
      } catch {
        return null;
      }
    }

    function showPhoto(index) {
      if (!photos.length) return;
      const selectedIndex = Math.max(0, Math.min(index, photos.length - 1));
      const selected = photos[selectedIndex];
      photoIndex = selectedIndex;
      counter.textContent = `${photoIndex + 1} / ${photos.length}`;
      previous.disabled = photoIndex === 0;
      next.disabled = photoIndex === photos.length - 1;
      previous.setAttribute('aria-disabled', String(previous.disabled));
      next.setAttribute('aria-disabled', String(next.disabled));
      image.alt = selected.alt || t('photos.altByAuthor', { author: reviewAuthor, index: photoIndex + 1 });
      if (selected.width > 0 && selected.height > 0) {
        image.width = selected.width;
        image.height = selected.height;
      } else {
        image.removeAttribute('width');
        image.removeAttribute('height');
      }
      if (image.getAttribute('src') === selected.src) return;
      viewport.setAttribute('aria-busy', 'true');
      image.classList.remove('has-error');
      setStatus(t('photos.loading'));
      image.src = selected.src;
      if (image.complete && image.naturalWidth > 0) onImageLoad();
    }

    function open(reviewIndex, requestedPhotoIndex = 0) {
      const index = Number(reviewIndex);
      if (!Number.isInteger(index) || index < 0 || index >= reviews.length) return false;
      const review = reviews[index];
      const selectedPhotos = Array.isArray(review?.photos) ? review.photos.map(normalizePhoto).filter(Boolean) : [];
      if (!selectedPhotos.length) return false;
      const requested = Number(requestedPhotoIndex);
      if (!isOpen()) {
        previouslyFocused = document.activeElement;
        previousOverflow = document.documentElement.style.overflow;
      }
      photos = selectedPhotos;
      reviewAuthor = typeof review.author === 'string' && review.author.trim() ? review.author.trim() : t('reviews.anonymous');
      author.textContent = t('photos.byAuthor', { author: reviewAuthor });
      showPhoto(Number.isInteger(requested) ? requested : 0);
      if (!isOpen()) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.documentElement.style.overflow = 'hidden';
        scrollLocked = true;
        closeButton.focus({ preventScroll: true });
      }
      return true;
    }

    function onImageLoad() {
      viewport.setAttribute('aria-busy', 'false');
      image.classList.remove('has-error');
      setStatus('');
    }
    function onImageError() {
      viewport.setAttribute('aria-busy', 'false');
      image.classList.add('has-error');
      setStatus(t('photos.error'));
    }
    function onPageClick(event) {
      const button = event.target.closest?.('button[data-review-photo]');
      if (!button || !page.contains(button)) return;
      if (open(button.dataset.reviewIndex, button.dataset.photoIndex)) event.preventDefault();
    }
    function onPrevious() { showPhoto(photoIndex - 1); }
    function onNext() { showPhoto(photoIndex + 1); }
    function onKeyDown(event) {
      if (!isOpen()) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); onPrevious(); }
      if (event.key === 'ArrowRight') { event.preventDefault(); onNext(); }
      if (event.key === 'Escape') { event.preventDefault(); close(); }
    }
    function onBackdropClick(event) {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
    }
    function onCancel(event) { event.preventDefault(); close(); }
    function onTouchStart(event) {
      touchStart = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY, id: event.touches[0].identifier } : null;
    }
    function onTouchMove(event) { if (event.touches.length !== 1) touchStart = null; }
    function onTouchEnd(event) {
      if (!touchStart) return;
      const end = [...event.changedTouches].find((touch) => touch.identifier === touchStart.id);
      if (end) {
        const dx = end.clientX - touchStart.x;
        const dy = end.clientY - touchStart.y;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) showPhoto(photoIndex + (dx < 0 ? 1 : -1));
      }
      touchStart = null;
    }
    function onTouchCancel() { touchStart = null; }

    page.addEventListener('click', onPageClick);
    image.addEventListener('load', onImageLoad);
    image.addEventListener('error', onImageError);
    previous.addEventListener('click', onPrevious);
    next.addEventListener('click', onNext);
    closeButton.addEventListener('click', close);
    dialog.addEventListener('keydown', onKeyDown);
    dialog.addEventListener('click', onBackdropClick);
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('close', restoreFocusAndScroll);
    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: true });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true });
    viewport.addEventListener('touchcancel', onTouchCancel, { passive: true });

    const controller = {
      open,
      close,
      destroy() {
        close();
        restoreFocusAndScroll();
        page.removeEventListener('click', onPageClick);
        image.removeEventListener('load', onImageLoad);
        image.removeEventListener('error', onImageError);
        previous.removeEventListener('click', onPrevious);
        next.removeEventListener('click', onNext);
        closeButton.removeEventListener('click', close);
        dialog.removeEventListener('keydown', onKeyDown);
        dialog.removeEventListener('click', onBackdropClick);
        dialog.removeEventListener('cancel', onCancel);
        dialog.removeEventListener('close', restoreFocusAndScroll);
        viewport.removeEventListener('touchstart', onTouchStart);
        viewport.removeEventListener('touchmove', onTouchMove);
        viewport.removeEventListener('touchend', onTouchEnd);
        viewport.removeEventListener('touchcancel', onTouchCancel);
        instances.delete(page);
      }
    };
    instances.set(page, controller);
    return controller;
  }

  window.JblReviewMedia = { create };
})();
